import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { useAuth } from '../context/AuthContext';
import { Bars3Icon } from "@heroicons/react/24/outline";

const Layout = () => { 
  const { user, permissions } = useAuth();
  const location = useLocation();
  
  // Sidebar state - start closed on mobile, open on desktop
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  // Handle window resize to auto-open/close based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
      if (user) {
          console.log("🛠️ --- DEBUG: PERMISSION CHECK --- 🛠️");
          console.log("👤 User Name:", user.name);
          console.log("🔰 User Role:", user.role);
      }
  }, [user, permissions]);

  return (
    <div className="bg-[#F8FAFC] dark:bg-[#0d1117] min-h-screen font-sans flex flex-col overflow-x-hidden selection:bg-orange-100 selection:text-orange-900 transition-colors duration-200"> 
      
      {/* UNIFIED HEADER - Material Clean White (Matching Reference Design) */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-[#161b22] text-slate-800 dark:text-[#f0f6fc] border-b border-slate-200/80 dark:border-[#30363d] z-[100] flex items-center px-3 sm:px-4 lg:px-6 shadow-2xs transition-colors duration-200 overflow-visible">
        
        {/* Hamburger */}
        <div className="flex items-center shrink-0 mr-2 sm:mr-3">
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl border border-slate-200/80 dark:border-[#30363d]/80 bg-white dark:bg-[#161b22] hover:bg-slate-50 dark:hover:bg-[#21262d] text-slate-600 dark:text-[#848d97] hover:text-slate-900 dark:hover:text-white transition-all duration-200 shadow-2xs hover:shadow-xs active:scale-95 flex items-center justify-center cursor-pointer"
            title="Toggle Sidebar"
            aria-label="Toggle Sidebar"
          >
            <Bars3Icon className="w-5 h-5 shrink-0" />
          </button>
        </div>

        {/* Topbar — takes remaining space, min-w-0 prevents overflow while allowing anchored dropdowns */}
        <div className="flex-1 h-full flex items-center min-w-0 overflow-visible">
          <Topbar />
        </div>
      </header>

      {/* OVERLAY FOR MOBILE (closes sidebar when clicking outside) */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR COMPONENT - Pass down the close function */}
      <Sidebar isOpen={isSidebarOpen} closeSidebar={() => setIsSidebarOpen(false)} />

      {/* MAIN CONTENT AREA
          - FIX: Changed dynamic tailwind class to exact strings for proper compiling
          - lg:ml-64 ensures it pushes content on desktop when open
      */}
      <main className={`
        pt-16 flex-1 flex flex-col min-h-screen transition-all duration-300 ease-in-out
        overflow-x-hidden w-full
        ${isSidebarOpen ? 'lg:ml-64 lg:w-[calc(100%-16rem)]' : 'lg:ml-0'}
      `}>
        <div className="p-2 sm:p-4 flex-1 w-full max-w-full overflow-x-hidden">
          <Outlet /> 
        </div>
      </main>
      
    </div>
  );
};

export default Layout;